/**
 * Chart specs the agent can produce, built from this site's own data.
 *
 * These return Flint `ChartAssemblyInput` objects — a semantic description of
 * what the chart MEANS (fields, semantic types, chart type) rather than pixel
 * instructions. Flint derives scales, axes, spacing and labels from that.
 * See https://github.com/microsoft/flint-chart (MIT).
 *
 * Everything here computes from `allArticles`, so the agent cannot invent
 * numbers: it chooses which question to ask, not what the answer is.
 */

import { allArticles } from '../Components/Data/articles';

export type Dataset = 'articles_by_category' | 'articles_by_month' | 'article_lengths';

export interface FlintInput {
    data: { values: Record<string, unknown>[] };
    semantic_types: Record<string, string>;
    chart_spec: {
        chartType: string;
        encodings: Record<string, { field: string }>;
        baseSize?: { width: number; height: number };
    };
    theme_spec?: Record<string, unknown>;
}

export interface ChartBuild {
    input: FlintInput;
    title: string;
    /** Plain-language summary of what the chart shows, for the transcript */
    caption: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthKey(dateSort: string): string {
    // dateSort is YYYY-MM-DD
    const [y, m] = dateSort.split('-');
    const mi = Number(m) - 1;
    return `${MONTHS[mi] ?? m} ${y}`;
}

export const DATASETS: Record<Dataset, string> = {
    articles_by_category: 'how many articles sit in each category',
    articles_by_month: 'how many articles were published each month',
    article_lengths: 'how long each article is, in words',
};

export function buildChart(dataset: Dataset, size = { width: 460, height: 260 }): ChartBuild {
    switch (dataset) {
        case 'articles_by_month': {
            const counts = new Map<string, number>();
            for (const a of allArticles) {
                if (!a.dateSort) continue;
                const k = monthKey(a.dateSort);
                counts.set(k, (counts.get(k) ?? 0) + 1);
            }
            // chronological, not insertion order
            const values = Array.from(counts.entries())
                .map(([month, articles]) => ({ month, articles }))
                .sort((x, y) => {
                    const p = (s: string) => {
                        const [mo, yr] = s.split(' ');
                        return Number(yr) * 12 + MONTHS.indexOf(mo);
                    };
                    return p(x.month) - p(y.month);
                });
            return {
                title: 'Articles published per month',
                caption: `${allArticles.length} articles across ${values.length} months`,
                input: {
                    data: { values },
                    semantic_types: { month: 'Time', articles: 'Quantity' },
                    chart_spec: {
                        chartType: 'Bar Chart',
                        encodings: { x: { field: 'month' }, y: { field: 'articles' } },
                        baseSize: size,
                    },
                },
            };
        }
        case 'article_lengths': {
            const values = allArticles
                .map(a => ({ title: a.title.slice(0, 34), words: a.content.split(/\s+/).length }))
                .sort((x, y) => y.words - x.words)
                .slice(0, 10);
            return {
                title: 'Longest articles, by word count',
                caption: `top ${values.length} of ${allArticles.length} by length`,
                input: {
                    data: { values },
                    semantic_types: { title: 'Name', words: 'Quantity' },
                    chart_spec: {
                        chartType: 'Bar Chart',
                        encodings: { x: { field: 'words' }, y: { field: 'title' } },
                        baseSize: { width: size.width, height: 320 },
                    },
                },
            };
        }
        case 'articles_by_category':
        default: {
            const counts = new Map<string, number>();
            for (const a of allArticles) {
                counts.set(a.category, (counts.get(a.category) ?? 0) + 1);
            }
            const values = Array.from(counts.entries())
                .map(([category, articles]) => ({ category, articles }))
                .sort((x, y) => y.articles - x.articles);
            return {
                title: 'Articles by category',
                caption: `${allArticles.length} articles across ${values.length} categories`,
                input: {
                    data: { values },
                    semantic_types: { category: 'Name', articles: 'Quantity' },
                    chart_spec: {
                        chartType: 'Bar Chart',
                        encodings: { x: { field: 'category' }, y: { field: 'articles' } },
                        baseSize: size,
                    },
                },
            };
        }
    }
}
